<template>
  <page-wrapper error-key="roomCategories">
    <template #title>
      <div class="h3">Категории номеров</div>
    </template>

    <template v-if="isLoading || roomCategoriesList?.length">
      <div class="d-flex mb-6">
        <room-categories-filter v-model="filter" :types="filterTypes" class="w-100 mr-auto" />
        <e-btn
          v-if="!isOperationHidden(roomCategories?.operations?.canAdd)"
          prepend-icon-src="plus"
          :icon="$vuetify.display.smAndDown"
          :disbled="isOperationDisabled(roomCategories?.operations?.canAdd)"
          variant="primary"
          class="add-room-category-button"
          @click="setIsDialogOpen(true)"
        >
          <template v-if="!$vuetify.display.smAndDown"> Добавить</template>
        </e-btn>
      </div>

      <e-data-items
        :headers="tableHeaders"
        :items="filteredRoomCategories"
        :loading="isLoading"
        interactive
        content-wrap
        @on-row-click="handleRowClick"
      >
        <template v-slot:[`item.actions`]="{ item }">
          <room-categories-row-context
            :delete-operation-status="item.operations.delete"
            :edit-operation-status="item.operations.edit"
            @edit="handleRowEdit(item)"
            @remove="handleRowRemove(item)"
          />
        </template>
      </e-data-items>
    </template>

    <page-content-placeholder v-else text="У вас отсутствуют категории номеров" :img-src="Placeholder">
      <e-btn
        v-if="!isOperationHidden(roomCategories?.operations?.canAdd)"
        prepend-icon-src="plus"
        variant="primary"
        class="mt-6"
        @click="setIsDialogOpen(true)"
      >
        Добавить категорию номера
      </e-btn>
    </page-content-placeholder>

    <room-category-dialog
      v-model="isDialogOpen"
      :room-category="selectedRoomCategory"
      @after-leave="handleDialogAfterLeave"
      @success:add="() => getRoomCategories()"
      @success:update="() => getRoomCategories()"
      @success:remove="getRoomCategories"
    />

    <remove-room-category-dialog
      v-model="isRemoveDialogOpen"
      :room-category-id="selectedRoomCategory?.cid"
      @remove-success="getRoomCategories"
    />
  </page-wrapper>
</template>

<script setup lang="ts">
import { computed, reactive, ref, toRef } from 'vue';
import { useDisplay } from 'vuetify';
import type { RoomType } from '@/graphql/generated';
import type { RoomCategoriesItemType } from '@/parts/room-categories/store/roomCategories';
import Placeholder from '@/assets/img/pages/room-categories/no-room-categories.svg';
import PageContentPlaceholder from '@/core/components/common/PageContentPlaceholder.vue';
import EDataItems from '@/core/components/common/data-items/EDataItems.vue';
import PageWrapper from '@/core/components/interface/PageWrapper.vue';
import { EBtn } from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import useToggle from '@/core/composables/useToggle';
import { getSelectFilterItems } from '@/core/utils/filters';
import { isOperationDisabled, isOperationHidden } from '@/core/utils/permissions';
import { containsSubstring } from '@/core/utils/string';
import RemoveRoomCategoryDialog from '@/parts/room-categories/components/RemoveRoomCategoryDialog.vue';
import RoomCategoriesFilter from '@/parts/room-categories/components/RoomCategoriesFilter.vue';
import RoomCategoriesRowContext from '@/parts/room-categories/components/RoomCategoriesRowContext.vue';
import RoomCategoryDialog from '@/parts/room-categories/components/RoomCategoryDialog.vue';
import { useRoomCategoriesStore } from '@/parts/room-categories/store/roomCategories';

const display = useDisplay();
const [isDialogOpen, setIsDialogOpen] = useToggle();
const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useToggle();
const { roomCategories, getRoomCategories } = useRoomCategoriesStore();

const filter = reactive<{
  query: string;
  type: RoomType | null;
}>({
  query: '',
  type: null,
});

const selectedRoomCategory = ref<RoomCategoriesItemType>();

const roomCategoriesList = toRef(() => roomCategories.value.roomCategories.data);

const filterTypes = computed(() => getSelectFilterItems(roomCategoriesList.value || [], 'cid', 'type'));

const tableHeaders = computed(() => [
  {
    key: 'name[0].value',
    title: 'Наименование',
    width: '49%',
  },
  {
    key: 'type.name',
    title: 'Тип',
  },
  {
    key: 'seat.baseQuantity',
    title: 'Осн. мест',
    sortable: false,
    width: display.mdAndDown.value ? 86 : 160,
  },
  {
    key: 'seat.extQuantity',
    title: 'Доп. мест',
    sortable: false,
    width: display.mdAndDown.value ? 86 : 160,
  },
  {
    key: 'actions',
    sortable: false,
    width: display.mdAndDown.value ? 40 : 68,
  },
]);

const filteredRoomCategories = computed(() => {
  if (!roomCategoriesList.value) return [];

  return roomCategoriesList.value.filter((item) => {
    if (filter.query && !item.name.some((nameItem) => containsSubstring(nameItem.value, filter.query))) {
      return false;
    }

    if (filter.type && item.type.cid !== filter.type?.cid) {
      return false;
    }

    return true;
  });
});

const handleRowClick = (_: Event, row: { item: RoomCategoriesItemType }) => {
  selectedRoomCategory.value = row.item;
  setIsDialogOpen(true);
};

const handleRowEdit = (item: RoomCategoriesItemType) => {
  selectedRoomCategory.value = item;
  setIsDialogOpen(true);
};

const handleRowRemove = (item: RoomCategoriesItemType) => {
  selectedRoomCategory.value = item;
  setIsRemoveDialogOpen(true);
};

const handleDialogAfterLeave = () => {
  selectedRoomCategory.value = undefined;
};

const { isLoading } = useFetch(async () => {
  await getRoomCategories();
});
</script>

<style scoped lang="scss">
.add-room-category-button {
  @include md-and-up {
    margin-left: 16px;
  }

  @include sm-and-down {
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: $z-index-3;
  }

  @include xs {
    right: 16px;
    bottom: 16px;
  }
}
</style>
